<script setup lang="ts">
import { storeToRefs } from 'pinia';
import DailyCheckinDayCell from './DayCell.vue';
import DailyCheckinClaimPopup from './ClaimPopup.vue';
import { usePlayerProfileStore } from '@/stores/player-profile';
import { GAMIFICATION_SLUGS } from '@/types/player-profile';
import { levelGateMessage } from '@/utils/apostatudo/crm-level-slug';
import { useDailyCheckinStore } from '@/stores/daily-checkin';
import {
  endOfOperationDay,
  nextMidnightInOperationTz,
} from '@/utils/apostatudo/operation-day';
import { getAssetsUrlForCDN, localeLink } from '@/utils/template';
import { useMarketingOptIn } from '@/composables/useMarketingOptIn';
import { useLoyaltyHubStore } from '@/stores/loyalty-hub';

// Modal "Recompensas Diárias" (Check-in Diário — épico ENG-424). Mesma
// mecânica da roletinha e do hub: Teleport pro body, slide-up por @keyframes
// (o reset global de transition em main.scss mata `transition`), e a store
// controla a abertura sem navegar de rota.
//
// A trilha é sequencial: cada célula é um DEGRAU, não um dia do mês.
//
// Figma: file 8oInBlwexpWshczTeB5rJg → "Componentes" → "Modal" (382x571).
const store = useDailyCheckinStore();
const {
  isOpen,
  status,
  board,
  rewardViews,
  claiming,
  claimError,
  selectedReward,
} = storeToRefs(store);

// Logo e número de colunas são decisões do front. A copy do (i) já foi uma
// delas: hoje o texto é o regulamento cadastrado no CMS (`period.terms`), e o
// texto abaixo sobrou como FALLBACK — sem ele, um período publicado sem
// regulamento abriria o popover em branco.
const LOGO = '/assets/images/daily-checkin/logo.webp';
const COLUMNS = 5;
// Cor de destaque do sheet: as partículas do fundo. O painel é um gradiente azul-escuro (#00005c → #00001b), então o dourado é o
// que aparece — e é o contraponto do azul que a roletinha usa nos mesmos lugares.
const ACCENT_COLOR = '#FFD05C';

const INFO_FALLBACK =
  'As recompensas são uma trilha: a cada dia você resgata o próximo degrau. Faltar um dia não perde nenhum prêmio — só atrasa seu avanço, e o período tem data para acabar.';

// `visible` mantém o overlay montado até o slide-down terminar; `sheetOpen`
// dispara a animação.
const visible = ref(false);
const sheetOpen = ref(false);

// Popover do botão (i): regulamento do período, do CMS.
const infoOpen = ref(false);

// Sem board (erro, ou período sem regulamento cadastrado) o (i) cai neste
// fallback, que explica a mecânica sem afirmar nada sobre um período específico.
// Os estados sem acesso não passam por aqui: quem sobe neles é o modal de aviso,
// que não tem (i) — a própria tela já diz o motivo.
const infoText = computed(
  () => board.value?.period?.terms?.trim() || INFO_FALLBACK,
);

// O campo é textarea no CMS, então o normal é texto puro com '\n' — e é o
// `pre-line` que transforma essas quebras em parágrafos. Mas o mesmo campo na
// roletinha às vezes chega com HTML, e aí `pre-line` viraria a indentação do
// markup em linhas vazias: quando há tag, o texto é renderizado como HTML e o
// espaçamento fica com ele.
const infoIsHtml = computed(() => /<[a-z][^>]*>/i.test(infoText.value));

useBodyScrollLock(visible);

const logoImage = computed(() => getAssetsUrlForCDN(LOGO));

/* ---------------- modal de aviso ---------------- */

// Estados que NÃO abrem a trilha e sim o modal de aviso (BaseNoticeSheet): sem
// logo, sem (i), sem grade — arte de cabeçalho e o motivo. Estes três, e não
// 'error': os três são resposta legítima do servidor, e 'error' continua dentro
// do painel com o "Tentar novamente" ao lado da frase.
//
// O 'disabled' (ENG-793) precisa estar aqui, e não só por desenho: sem ele o
// estado escorre pro `v-else` do template e o jogador vê a GRADE VAZIA, que é
// como o board desligado chega do core.
const isNotice = computed(
  () =>
    status.value === 'unavailable' ||
    status.value === 'ineligible' ||
    status.value === 'disabled',
);

// Aviso de nível (o mesmo da loja) no estado de elegibilidade: abaixo do degrau
// mínimo do check-in. Os slugs (mínimo e atual) vêm do /players/me, porque a
// régua é editada no backoffice do core — nada de constante local.
//
// Desde o ENG-787 o 'ineligible' tem uma origem só (o gate de nível), então não
// há mais o caso "veio do board": o `status` já é a guarda suficiente, e a copy
// de "usuários ativos na plataforma" morreu junto com o `is_eligible` da rota.
// Mesmo desenho da roletinha (RouletteGame.vue).
const playerProfileStore = usePlayerProfileStore();

const noticeLevelGate = computed(() =>
  status.value === 'ineligible'
    ? {
        subject: 'Seu check-in',
        minLevel: playerProfileStore.minLevelFor(
          GAMIFICATION_SLUGS.dailyCheckIn,
        ),
        currentLevel: playerProfileStore.crmLevel,
      }
    : null,
);

// A frase do aviso é o MOTIVO. No 'ineligible' ela é o título do aviso de
// nível (o degrau que falta); no 'unavailable' ela diz que não há trilha PRA
// ELE — o "para você" é a palavra que importa, e é a mesma da roletinha.
//
// Sem esse "para você" a frase falava do mundo ("não existe recompensa agora") e
// soava como jogo fora do ar, quando o caso mais comum é audiência: o período
// está rodando pra outro segmento.
//
// O 'disabled' (ENG-793) é o oposto e por isso fala do mundo mesmo: a jornada
// EXISTIU pra ele e terminou.
//
// Aqui vale a copy do DESENHO aprovado, e não a frase curta do card: o título
// olha pra frente ("volta em novo formato") em vez de anunciar um fim, que é o
// que a tela inteira defende — ela existe pra dizer que a promoção não morreu.
// A frase do card ("jornada finalizada") ficava contra a lista logo abaixo, que
// promete a jornada nova.
//
// O `check_in_disabled` do resgate acompanha esta frase (ver CLAIM_MESSAGES na
// store): é o mesmo fato pelos dois caminhos — o board e o clique —, e ler
// frases diferentes pro mesmo motivo, na mesma tela, é o que confunde.
const noticeMessage = computed(() => {
  if (status.value === 'disabled') return 'Volta em novo formato';

  return noticeLevelGate.value
    ? levelGateMessage(noticeLevelGate.value.minLevel)
    : 'Nenhuma recompensa diária disponível para você no momento.';
});

const noticeHint = computed(() =>
  status.value === 'disabled'
    ? 'A jornada antiga fechou aqui. O check-in está sendo refeito e reabre em breve com outra dinâmica.'
    : '',
);

// Selo só no 'disabled': é o único motivo que é uma PAUSA. No 'unavailable' não
// há ciclo pra esperar (o jogador está fora do segmento) e no 'ineligible' o que
// falta é nível, não tempo.
const noticeBadge = computed(() =>
  status.value === 'disabled' ? 'Aguardando nova jornada' : '',
);

// Sem segunda linha, igual à roletinha. Ela dizia "assim que um novo período
// começar, sua trilha aparece aqui" — promessa falsa pra quem está fora do
// segmento: período novo não muda a audiência dele.

// Cabeçalho do aviso: o calendário 3D do Figma (285:6919 / 287:7903), recortado
// na janela do desenho. Aqui não há versão por tier — a arte do check-in é uma
// só (a roletinha usa a roda do tier do jogador).
// No 'disabled' a arte é o selo "EM BREVE" (ENG-793), extraído do desenho: o
// calendário 3D dos outros dois motivos ilustra a promoção, e mostrá-lo aqui
// contradiz a frase logo abaixo dele.
const noticeArt = computed(() =>
  getAssetsUrlForCDN(
    status.value === 'disabled'
      ? '/assets/images/daily-checkin/em-breve.webp'
      : '/assets/images/daily-checkin/aviso-header.webp',
  ),
);

// O calendário sangra pro painel; o selo é peça fechada e aparece inteiro.
const noticeArtFit = computed(() =>
  status.value === 'disabled' ? ('emblem' as const) : ('bleed' as const),
);

// O que muda na jornada nova. Copy fixa (Head de CRM) e nao dado da rota: sao
// promessas do formato que vem, e nao o estado do jogador — o board do check-in
// desligado chega VAZIO, entao nao ha o que ler dele aqui.
// Icones inline (o `d` do desenho) e nao asset: sao quatro glifos de 24x24 que
// so existem nesta lista. Em arquivo seriam quatro requisicoes e uma pasta nova
// pra algo que nenhuma outra tela reusa.
const NEWS_ITEMS = [
  {
    key: 'bolt',
    text: 'Progresso mais rápido do começo ao fim',
    path: 'M13.6 2.2 4.4 13.4a.8.8 0 0 0 .6 1.3h4.6l-1.1 6.8a.6.6 0 0 0 1.1.45l9.1-11.2a.8.8 0 0 0-.6-1.3h-4.6l1.2-6.8a.6.6 0 0 0-1.1-.45Z',
  },
  {
    key: 'gift',
    text: 'Novas recompensas na jornada',
    path: 'M3.5 10.5h17v9a1.5 1.5 0 0 1-1.5 1.5h-14a1.5 1.5 0 0 1-1.5-1.5Zm-.5-4.2h18v3.2H3ZM11 6.3H7.4a2 2 0 1 1 2-2.9Zm2 0 1.6-2.9a2 2 0 1 1 2 2.9Z',
  },
  {
    key: 'trophy',
    text: 'Prêmios diferentes dos que você já viu aqui',
    path: 'M6 3h12v2.2h2.6v2.4a3.8 3.8 0 0 1-3.5 3.8 5.4 5.4 0 0 1-3.1 3.2V18h3.2v3H6.8v-3H10v-3.4a5.4 5.4 0 0 1-3.1-3.2A3.8 3.8 0 0 1 3.4 7.6V5.2H6Zm0 4.4h-.6v.2a2 2 0 0 0 .6 1.4Zm12 0v1.6a2 2 0 0 0 .6-1.4v-.2Z',
  },
  {
    key: 'rules',
    text: 'Regras mais simples de acompanhar',
    path: 'M4.4 5.2h15.2v2.2H4.4Zm0 5.7h15.2v2.2H4.4Zm0 5.7h10.4v2.2H4.4Z',
  },
];

// Acordeao do (i): o motivo da troca. Fechado por padrao — a folha ja responde
// "acabou, volta em breve" sem ele, e quem quer o porque pede.
const isWhyOpen = ref(false);

// Opt-in de notificacao: o MESMO `mkt_accepted_at` do perfil V2
// (composables/useMarketingOptIn), e nao um canal proprio. Reusar significa que
// ligar aqui aparece ligado la, e que o opt-out do perfil vale aqui — se fosse
// um flag so do check-in, o jogador teria dois interruptores pro mesmo aviso.
const {
  isActive: isNotifyActive,
  isSaving: isNotifySaving,
  setMarketing,
} = useMarketingOptIn();

const loyaltyHub = useLoyaltyHubStore();

// "Ir para o Clube de Fidelidade": fecha o check-in e abre o hub. Os dois sao
// modais irmaos montados no layout, entao nao ha rota pra navegar.
function goToLoyaltyHub() {
  close();
  loyaltyHub.open();
}

const asset = (file: string) =>
  getAssetsUrlForCDN(`/assets/images/daily-checkin/${file}`);

// A largura da célula sai da equação do grid: N células + (N-1) gaps, com o
// gap valendo 6/65 da célula (proporção do Figma) →
//   grid = célula * (71N - 6) / 65
// O divisor é calculado aqui, e não no CSS, pra não depender de divisão por
// expressão com var() dentro de calc().
const frameStyle = computed(() => ({
  '--dc-cell': `calc((var(--dc-panel-w) - 32px) / ${(71 * COLUMNS - 6) / 65})`,
}));

/* ---------------- virada do dia da operação ---------------- */

let midnightTimer: ReturnType<typeof setTimeout> | null = null;

function stopMidnightTimer() {
  if (midnightTimer) {
    clearTimeout(midnightTimer);
    midnightTimer = null;
  }
}

// UM timeout até a próxima meia-noite da operação — separado do tique de 1s do
// contador, que só desenha. Na virada a trilha é RECARREGADA, o que reabilita o
// resgate sem o jogador precisar fechar e abrir o modal.
//
// Tem que ser `refresh`: o board foi buscado há pouco (o modal está aberto),
// então o cache do handler devolveria o dia de ontem, com o resgate ainda
// fechado. E reagenda no fim, pro modal que fica aberto além de uma virada.
function scheduleMidnight() {
  stopMidnightTimer();

  // 1s de folga pra não acordar no último instante do dia velho: o relógio do
  // dispositivo não é o da operação, e um segundo antes significaria recarregar
  // o board pra ler o mesmo dia de novo.
  const delay = Date.parse(nextMidnightInOperationTz()) - Date.now() + 1000;

  midnightTimer = setTimeout(
    () => {
      store.refresh();
      scheduleMidnight();
    },
    Math.max(1000, delay),
  );
}

/* ---------------- reset do calendário ---------------- */

// Instante EXATO em que a trilha reseta: a meia-noite que fecha o `endsOn` (o
// último dia válido do ciclo), no fuso da operação. É o mesmo marco que a
// contagem em dias usava — só que agora em milissegundos, porque a faixa mostra
// hora, minuto e segundo.
const resetAt = computed(() => {
  const endsOn = board.value?.period?.endsOn;
  const iso = endsOn ? endOfOperationDay(endsOn) : null;
  const ms = iso ? Date.parse(iso) : NaN;

  return Number.isNaN(ms) ? null : ms;
});

// Relógio do contador. Precisa ser um ref pra tela reagir: `Date.now()` dentro
// do computed não é reativo, então sem ele a linha congelaria no valor do
// primeiro render.
const now = ref(Date.now());

let tickTimer: ReturnType<typeof setInterval> | null = null;

function stopTicker() {
  if (tickTimer) {
    clearInterval(tickTimer);
    tickTimer = null;
  }
}

// Tique de 1s, ligado só enquanto o modal está na tela (o `onClosed` desliga):
// é a única coisa aqui que acorda o render de segundo em segundo, e ela não tem
// o que fazer com o painel fechado.
//
// Lê o relógio na hora de ligar porque o modal pode reabrir minutos depois — sem
// isso o primeiro segundo mostraria a conta velha.
function startTicker() {
  stopTicker();
  now.value = Date.now();
  tickTimer = setInterval(() => {
    now.value = Date.now();
  }, 1000);
}

const pad = (value: number) => String(value).padStart(2, '0');

// "O Check-in Diário reseta em 12d 05h 32m 10s". O bloco de dias só aparece
// quando há dias — "0d" ocuparia espaço sem informar nada.
//
// `Math.ceil` e não `floor`: com truncamento, o último segundo do ciclo apareceria
// como 00h 00m 00s por um segundo inteiro, um relógio zerado com a trilha ainda
// jogável. Arredondando pra cima, a linha vai até 01s e some quando o prazo
// vence de fato.
//
// Prazo vencido (ou `ends_on` ilegível) devolve string vazia: sem linha, em vez
// de contagem negativa. Ciclo encerrado de verdade tem quem avise — o estado
// 'unavailable', depois que o board recarregar na virada.
const resetNote = computed(() => {
  if (resetAt.value === null) return '';

  const remaining = Math.ceil((resetAt.value - now.value) / 1000);

  if (remaining <= 0) return '';

  const days = Math.floor(remaining / 86_400);
  const hours = Math.floor((remaining % 86_400) / 3_600);
  const minutes = Math.floor((remaining % 3_600) / 60);
  const seconds = remaining % 60;

  const clock = `${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;

  return `O Check-in Diário reseta em ${days > 0 ? `${days}d ` : ''}${clock}`;
});

/* ---------------- próximo resgate ---------------- */

// Instante em que o PRÓXIMO resgate libera. Só existe com o dia já consumido:
// com o resgate de hoje aberto não há o que esperar — e é exatamente quando o
// core manda `next_claim_available_at` null. Trilha sem degrau restante também
// não tem próximo resgate.
//
// O fallback na próxima meia-noite da operação cobre o campo faltando no
// payload: é a mesma regra que o core aplica pra preencher o campo.
const nextClaimAt = computed(() => {
  const current = board.value;

  if (!current || current.canClaimToday || current.remainingCount <= 0) {
    return null;
  }

  const iso = current.nextClaimAvailableAt ?? nextMidnightInOperationTz();
  const ms = Date.parse(iso);

  if (Number.isNaN(ms)) return null;

  // No último dia do ciclo a próxima meia-noite É o reset: não existe resgate
  // depois dela, e quem fala desse marco é a linha de cima.
  return resetAt.value !== null && ms >= resetAt.value ? null : ms;
});

// "Próximo resgate em 05h 32m 10s". Mesmo `Math.ceil` do resetNote, pelo mesmo
// motivo: truncando, o último segundo da espera mostraria 00h 00m 00s com o
// resgate ainda fechado. E quando o relógio zera, quem troca a linha pelo botão
// laranja é o reload da virada (scheduleMidnight) — a linha só some.
const nextClaimNote = computed(() => {
  if (nextClaimAt.value === null) return '';

  const remaining = Math.ceil((nextClaimAt.value - now.value) / 1000);

  if (remaining <= 0) return '';

  const hours = Math.floor(remaining / 3_600);
  const minutes = Math.floor((remaining % 3_600) / 60);
  const seconds = remaining % 60;

  return `Próximo resgate em ${pad(hours)}h ${pad(minutes)}m ${pad(seconds)}s`;
});

// Overlay e painel sobem no MESMO passo de render. Antes o painel esperava um
// `nextTick` (a `<Transition>` só anima o filho que aparece DEPOIS dela montar,
// e sem isso não haveria slide-up). O `appear` compra o mesmo efeito na
// primeira renderização, então o painel começa a andar junto com o backdrop em
// vez de um quadro atrás — e esse quadro custa caro, porque é o mesmo em que o
// backdrop borrado é composto pela primeira vez.
watch(
  isOpen,
  (opened) => {
    if (opened) {
      visible.value = true;
      infoOpen.value = false;
      scheduleMidnight();
      startTicker();
      sheetOpen.value = true;
    } else {
      sheetOpen.value = false;
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  stopMidnightTimer();
  stopTicker();
});

// `@after-leave` do <Transition> dispara em DOIS casos, e só um é fechamento:
// quando o sheet desce (isOpen → false) e quando o filho da transição TROCA com
// o modal aberto — o painel da trilha saindo pra entrar o BaseNoticeSheet, que
// é o que acontece quando o board chega 'ineligible'/'unavailable' depois do
// skeleton. Tratar a troca como fechamento derrubava `visible` e resetava a
// store com `isOpen` ainda true: o modal piscava e sumia, e o próximo open()
// (isOpen true → true) não acordava o watch — nunca mais abria.
function onClosed() {
  if (isOpen.value) return;

  visible.value = false;
  infoOpen.value = false;
  stopMidnightTimer();
  stopTicker();
  store.reset();
}

function close() {
  store.close();
}

// CTA do popup (USAR). Fechar o modal ANTES de navegar não é cortesia: o painel
// é teleportado pro body, então numa navegação interna (SPA) ele continuaria
// montado por cima da rota nova. O hub do Club Fidelidade, quando está aberto
// atrás, se fecha sozinho no watch de rota dele.
//
// URL absoluta navega externo; caminho interno passa por localeLink pra herdar o
// prefixo de locale — mesma regra do CTA da roletinha.
function onUse(link: string) {
  close();

  if (/^https?:\/\//i.test(link)) {
    navigateTo(link, { external: true });

    return;
  }

  navigateTo(localeLink(link));
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="dc-overlay"
      @dragstart.prevent
    >
      <div
        class="dc-backdrop"
        :class="{ 'dc-backdrop--out': !sheetOpen }"
        @click="close"
      />

      <Transition
        appear
        name="dc-slide-up"
        @after-leave="onClosed"
      >
        <!-- Sem trilha pra mostrar, sobe o modal de aviso no lugar do painel: a
             logo, o (i) e a grade não têm o que fazer nesses estados. -->
        <BaseNoticeSheet
          v-if="sheetOpen && isNotice"
          :accent="ACCENT_COLOR"
          :art="noticeArt"
          :art-fit="noticeArtFit"
          :badge="noticeBadge"
          :headline="status === 'disabled'"
          :hint="noticeHint"
          :level-gate="noticeLevelGate"
          :message="noticeMessage"
          :show-info="status === 'disabled'"
          @close="close"
          @navigate="close"
          @toggle-info="isWhyOpen = !isWhyOpen"
        >
          <template v-if="status === 'disabled'">
            <ul class="dc-news">
              <li
                v-for="item in NEWS_ITEMS"
                :key="item.key"
                class="dc-news__item"
              >
                <span
                  aria-hidden="true"
                  class="dc-news__icon"
                >
                  <svg
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path :d="item.path" />
                  </svg>
                </span>
                {{ item.text }}
              </li>
            </ul>

            <!-- Acordeao do (i). `v-show` e nao `v-if`: o bloco entra e sai o
                 tempo todo no mesmo modal, e remontar perderia a altura que a
                 animacao precisa medir. -->
            <div
              v-show="isWhyOpen"
              class="dc-why"
            >
              <p>
                Antes o check-in acompanhava o mês inteiro: era preciso marcar
                presença todo dia para chegar ao fim. Agora cada jornada passa a
                ter um período próprio, com começo e fim definidos por rodada.
              </p>
              <p>
                O que você já resgatou nas jornadas anteriores continua seu. A
                mudança vale a partir da próxima.
              </p>
            </div>

            <div class="dc-acts">
              <!-- Dois estados do MESMO opt-in: desligado convida, ligado
                   confirma. O confirmado nao e botao — nao ha o que apertar, e
                   deixa-lo clicavel convidaria a desligar bem no lugar que
                   acabou de pedir pra avisar. -->
              <div
                v-if="isNotifyActive"
                class="dc-notify dc-notify--done"
              >
                <img
                  alt=""
                  aria-hidden="true"
                  :src="asset('check.svg')"
                />
                <span>
                  <b>Suas notificações estão ativas</b>
                  <small>Você será informado quando iniciar a jornada</small>
                </span>
              </div>

              <button
                v-else
                class="dc-notify dc-notify--cta"
                :disabled="isNotifySaving"
                type="button"
                @click="setMarketing(true)"
              >
                <!-- Sino inline, como os icones da lista: e o glifo do desenho e
                     so este botao usa. `currentColor` pra ele seguir o texto
                     escuro do botao amarelo. -->
                <svg
                  aria-hidden="true"
                  class="dc-notify__bell"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M12 2.4a6.4 6.4 0 0 0-6.4 6.4v3.5L3.9 15a1 1 0 0 0 .85 1.55h14.5A1 1 0 0 0 20.1 15l-1.7-2.7V8.8A6.4 6.4 0 0 0 12 2.4Zm0 19.2a3 3 0 0 0 2.9-2.2H9.1a3 3 0 0 0 2.9 2.2Z"
                  />
                </svg>
                Quero saber quando iniciar a jornada
              </button>

              <button
                class="dc-hub"
                type="button"
                @click="goToLoyaltyHub"
              >
                Ir para o Clube de Fidelidade
              </button>
            </div>

            <!-- Fecha a tela respondendo a pergunta que ela levanta ("perdi o
                 que eu tinha?"). Sem numero: o board do check-in desligado chega
                 vazio, entao o front nao sabe quanto foi resgatado — e a frase
                 vale igual sem a conta. -->
            <p class="dc-foot">
              Seus resgates anteriores continuam disponíveis na sua conta.
            </p>
          </template>
        </BaseNoticeSheet>

        <div
          v-else-if="sheetOpen"
          class="dc-frame"
          :style="frameStyle"
        >
          <!-- Logo sangra pra cima do painel (fica metade fora), por isso vive
               no frame e não dentro do painel, que é clipado. -->
          <img
            alt="Check-in Diário"
            class="dc-logo"
            :src="logoImage"
          />

          <div class="dc-panel">
            <!-- Partículas douradas no fundo do painel, atrás de tudo. Douradas
                 pra contrastar com o azul do gradiente e pra não repetir o azul
                 da roletinha, que usa a mesma camada. -->
            <!-- `100vh` e não um valor em px: este painel é `max-height: 100%`
                 do overlay, então a altura dele é a do aparelho e um px fixo
                 criaria um teto invisível onde as partículas somem no meio da
                 tela. A viewport é sempre >= o painel, então elas saem pelo topo
                 em qualquer altura. (A roletinha usa o padrão de 700px porque o
                 sheet dela é capado em 620px.) -->
            <BaseParticlesField
              class="dc-particles"
              :color="ACCENT_COLOR"
              travel="100vh"
            />

            <button
              aria-label="Como funciona"
              class="dc-icon dc-icon--info"
              type="button"
              @click="infoOpen = true"
            >
              <img
                alt=""
                :src="asset('info.svg')"
              />
            </button>

            <button
              aria-label="Fechar"
              class="dc-icon dc-icon--close"
              type="button"
              @click="close"
            >
              <img
                alt=""
                :src="asset('close.svg')"
              />
            </button>

            <div
              v-if="status === 'loading' || status === 'idle'"
              class="dc-state"
            >
              Carregando check-in diário...
            </div>

            <!-- 'unavailable' e 'ineligible' não chegam aqui: quem sobe nesses
                 estados é o BaseNoticeSheet, no lugar deste painel. -->
            <div
              v-else-if="status === 'error'"
              class="dc-state"
            >
              <!-- Mesma copy da roletinha no mesmo cenário (core fora): os dois
                   modais do Clube Fidelidade falham com a mesma frase. -->
              <p>Serviço indisponível no momento. Tente novamente.</p>
              <button
                class="dc-retry"
                type="button"
                @click="store.refresh()"
              >
                Tentar novamente
              </button>
            </div>

            <template v-else>
              <!-- Faixa de estado: o prazo do ciclo e, com o dia já consumido,
                   a contagem pro próximo resgate. O "seu resgate de hoje está
                   liberado" saiu — quem anuncia o resgate liberado é o próprio
                   degrau laranja com o RESGATAR, e a frase repetia em texto o
                   que a trilha já mostra (por decisão de produto). Os `v-if`
                   são o que evita parágrafo vazio, e a margem dele, quando não
                   há nada a mostrar. -->
              <p
                v-if="resetNote"
                class="dc-status"
              >
                {{ resetNote }}
              </p>

              <p
                v-if="nextClaimNote"
                class="dc-status dc-status--next-claim"
              >
                {{ nextClaimNote }}
              </p>

              <div
                class="dc-grid"
                :style="{
                  gridTemplateColumns: `repeat(${COLUMNS}, minmax(0, 1fr))`,
                }"
              >
                <DailyCheckinDayCell
                  v-for="reward in rewardViews"
                  :key="reward.position"
                  :reward="reward"
                  @select="store.openReward"
                />
              </div>
            </template>
          </div>
        </div>
      </Transition>
    </div>
  </Teleport>

  <!-- Popup do prêmio / resgate, em overlay próprio por cima do calendário. -->
  <DailyCheckinClaimPopup
    :claiming="claiming"
    :error="claimError"
    :reward="selectedReward"
    @claim="store.claim()"
    @close="store.closeReward()"
    @use="onUse"
  />

  <!-- Modal do (i): regulamento do período (CMS), num overlay PRÓPRIO por
       cima do calendário — não mais um popover ancorado no botão, que cortava
       o texto quando a trilha tinha poucas células por cima. O backdrop e o X
       aqui fecham só este modal (infoOpen); o calendário atrás segue aberto. -->
  <Teleport to="body">
    <Transition
      appear
      name="dci-fade"
    >
      <div
        v-if="infoOpen"
        class="dci-overlay"
        @dragstart.prevent
      >
        <div
          class="dci-backdrop"
          @click="infoOpen = false"
        />

        <div class="dci-card">
          <button
            aria-label="Fechar"
            class="dci-close"
            type="button"
            @click="infoOpen = false"
          >
            <img
              alt=""
              :src="asset('close.svg')"
            />
          </button>

          <div
            v-if="infoIsHtml"
            class="dci-text"
            v-html="infoText"
          />
          <p
            v-else
            class="dci-text dci-text--plain"
          >
            {{ infoText }}
          </p>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<!-- Sem scope: @keyframes contornam o reset global de transition (mesma
     estratégia da roletinha e do hub). -->
<style>
.dc-slide-up-enter-active {
  animation: dc-slide-up 0.32s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  will-change: transform, opacity;
}
.dc-slide-up-leave-active {
  animation: dc-slide-up 0.24s cubic-bezier(0.4, 0, 1, 1) reverse forwards;
  will-change: transform, opacity;
}
@keyframes dc-slide-up {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

.dci-fade-enter-active {
  animation: dci-fade 0.18s ease-out forwards;
}
.dci-fade-leave-active {
  animation: dci-fade 0.14s ease-in reverse forwards;
}
@keyframes dci-fade {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>

<style scoped lang="scss">
// Empilha ACIMA do hub Club Fidelidade (z-9990) e do CashbackSheet (z-9995),
// abaixo da roletinha (z-9999) — os dois nunca aparecem juntos.
.dc-overlay {
  @apply fixed inset-0 z-[9996] flex flex-col justify-end select-none;

  img {
    -webkit-user-drag: none;
  }
}

// SEM `backdrop-filter` de propósito, ao contrário do hub e da roletinha: este
// modal quase sempre abre POR CIMA do hub, que já borra a página. Seriam dois
// blurs de tela cheia compostos ao mesmo tempo, e o quadro mais caro é
// justamente o primeiro — o do clique. O preto a 72% sozinho separa o painel do
// fundo; o que se perde é borrar a arte do hub atrás, que já está escurecida.
.dc-backdrop {
  @apply absolute inset-0;

  background: rgba(0, 0, 0, 0.72);
  animation: dc-fade-in 0.25s ease forwards;
}

.dc-backdrop--out {
  animation: dc-fade-in 0.24s ease reverse forwards;
}

@keyframes dc-fade-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

// Bottom-sheet colado no rodapé e nas laterais, igual à roletinha: a folha
// ocupa a largura toda até o teto de 480px (mesmo teto do .rlt-screen).

// Toda a peça é dimensionada a partir da largura do painel — 382px no Figma —
// então ela escala inteira, sem breakpoint. `--dc-u` vale 1px no tamanho de
// design e é a unidade usada aqui e no DayCell.
.dc-frame {
  --dc-panel-w: min(480px, 100vw);
  // Default de 5 colunas (Figma); o componente sobrescreve inline.
  --dc-cell: calc((var(--dc-panel-w) - 32px) / 5.3692);
  --dc-gap: calc(var(--dc-cell) * 6 / 65);
  --dc-u: calc(var(--dc-cell) / 65);

  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  width: var(--dc-panel-w);
  max-height: 100%;
  margin: 0 auto;
  // Reserva a parte do logo que fica acima do painel.
  padding-top: calc(64 * var(--dc-u));
}

// A proporção sai do arquivo, não de um número do Figma: o asset é exportado
// com recorte justo (434x296), então qualquer descasamento aqui distorceria a
// arte. `object-fit: contain` é o cinto de segurança pra quando a arte for
// trocada de novo e a proporção mudar antes deste valor acompanhar.
.dc-logo {
  position: absolute;
  z-index: 2;
  top: 0;
  left: 50%;
  width: calc(217 * var(--dc-u));
  transform: translateX(-50%);
  aspect-ratio: 434 / 296;
  object-fit: contain;
  pointer-events: none;
}

.dc-panel {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  // O padding de cima tem que limpar a parte do logo que invade o painel: o
  // logo tem 148u de altura (217u de largura na proporção do asset) e 64u
  // ficam acima do painel, sobrando 84u de sobreposição + 7u de folga.
  padding: calc(91 * var(--dc-u)) 16px
    calc(env(safe-area-inset-bottom, 0px) + 16px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  // Colado na base: só os cantos de cima arredondam e a borda de baixo some.
  border-bottom: 0;
  border-radius: 16px 16px 0 0;
  background: linear-gradient(180deg, #00005c 0%, #00001b 100%);
  // `clip` e não `hidden`: `hidden` é scroll container e o ajuste de foco do
  // clique desloca o painel (mesmo bug das bordas da roletinha).
  overflow: clip;
  // NÃO usar `isolation: isolate` aqui, por mais que ela resolva o empilhamento
  // das partículas: o painel viraria contexto próprio e prenderia o popover do
  // (i) dentro dele. Como o painel não tem z-index e a logo tem (z-2), o popover
  // (z-6) passaria a ser pintado ATRÁS da logo — a logo sangra por cima do painel
  // de propósito, e o popover abre justamente naquela faixa. A camada de
  // partículas resolve por elevação do conteúdo; ver `.dc-particles`.
}

// Partículas no rés do chão do painel e o conteúdo um degrau acima. É a
// alternativa ao `z-index: -1`, que exigiria isolar o painel e quebraria o
// popover do (i) (ver acima). O conteúdo em fluxo (`.dc-state`, `.dc-status`,
// `.dc-grid`) precisa de `position: relative` pra participar da ordem — sem
// posicionamento, uma camada em z-0 pinta por cima dele, por mais que ela venha
// antes no DOM. `relative` sem offset não muda layout nenhum.
.dc-particles {
  z-index: 0;
}

.dc-state,
.dc-status,
.dc-grid {
  position: relative;
  z-index: 1;
}

/* ---------- botões do topo ---------- */
// Assimetria do Figma preservada: (i) a 10px da esquerda, X a 21px da direita.
// z acima do scrim, pra o (i) seguir funcionando como toggle com o popover
// aberto (mesma ordem do topbar/scrim do dropdown ⋮ da roletinha).
.dc-icon {
  position: absolute;
  z-index: 5;
  top: calc(9 * var(--dc-u));
  width: calc(30 * var(--dc-u));
  height: calc(30 * var(--dc-u));

  img {
    @apply w-full h-full;
  }
}

.dc-icon--info {
  left: calc(10 * var(--dc-u));
}

.dc-icon--close {
  right: calc(21 * var(--dc-u));
}

/* ---------- modal do (i) ---------- */
// Mesma pilha do popup de resgate (.dcp-*): overlay próprio teleportado,
// acima do calendário (dc-overlay, z-9996), que fecha só a si mesmo — o
// backdrop e o X aqui nunca chamam o `close()` do calendário.
.dci-overlay {
  @apply fixed inset-0 z-[9997] flex items-center justify-center select-none;

  padding: 16px;
}

.dci-backdrop {
  @apply absolute inset-0;

  background: rgba(0, 0, 0, 0.6);
}

// Largura fixa e independente do painel (não usa --dc-u): o conteúdo é
// regulamento do CMS, de tamanho imprevisível, e o teto de altura é o que
// impede o card de cobrir a tela toda — quem rola é o texto.
.dci-card {
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  width: 300px;
  max-width: 100%;
  max-height: min(320px, 70vh);
  // 9 (topo do X) + 30 (altura) + 8 de folga: mesmo cálculo do popover antigo,
  // pra o texto começar abaixo do botão em vez de disputar a mesma linha.
  padding: 47px 16px 16px;
  border: 1px solid #203570;
  border-radius: 16px;
  background: #0c1b44;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.5);
}

.dci-close {
  @apply absolute;

  top: 9px;
  right: 12px;
  width: 30px;
  height: 30px;

  img {
    @apply w-full h-full;
  }
}

// O único scroll container do card. `min-height: 0` porque item de flex não
// encolhe abaixo do próprio conteúdo sem isso — e sem encolher, nada rola.
.dci-text {
  flex: 1;
  min-height: 0;
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  line-height: 1.4;
  overflow-y: auto;
  // Não encadeia o scroll pro body atrás.
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;

  // Vindo HTML do CMS: o preflight do Tailwind zera margem de parágrafo e
  // marcador de lista, então o espaçamento tem que ser declarado aqui. `:deep`
  // porque conteúdo de v-html não recebe o atributo do escopo.
  :deep(p + p),
  :deep(ul),
  :deep(ol) {
    margin-top: 6px;
  }

  :deep(ul),
  :deep(ol) {
    padding-left: 14px;
  }

  :deep(ul) {
    list-style: disc;
  }

  :deep(ol) {
    list-style: decimal;
  }

  :deep(li + li) {
    margin-top: 3px;
  }

  :deep(strong),
  :deep(b) {
    color: #fff;
    font-weight: 700;
  }

  :deep(a) {
    color: #7aa2ff;
    text-decoration: underline;
  }
}

// Texto puro: as quebras de linha do CMS são o único parágrafo que existe.
.dci-text--plain {
  white-space: pre-line;
}

/* ---------- estado do jogador ---------- */
// Prazo do ciclo e, com o dia já consumido, a contagem pro próximo resgate —
// mesma cara, uma embaixo da outra.
// `tabular-nums` fica: os relógios trocam de dígito a cada segundo, e sem ele
// as linhas dançam de largura ao trocar o número.
.dc-status {
  @apply shrink-0 text-center;

  margin-bottom: 10px;
  color: rgba(255, 255, 255, 0.75);
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

// Colada na linha do reset quando as duas aparecem: -6px come parte dos 10px
// de baixo da irmã (o painel é flex-column, margem não colapsa) e o vão fica
// em 4px — duas linhas do mesmo bloco, não dois blocos.
.dc-status + .dc-status--next-claim {
  margin-top: -6px;
}

/* ---------- trilha ---------- */
// Grid (e não flex-wrap): com flex, a linha só fecha se `N*célula + gaps`
// bater exatamente na largura, e o arredondamento sub-pixel jogava a última
// célula pra linha de baixo. Os degraus em destaque viram `span 2`.

// `minmax(0, 1fr)` e não `1fr`: o mínimo automático do `1fr` é o min-content
// das células, e a palavra "RESGATADO" empurrava as trilhas pra além da
// largura do grid. Com o mínimo zerado a trilha é puramente proporcional.
// A folga de 12u é pro anel de "hoje", que é desenhado FORA da célula: o grid
// rola, e todo scroll container corta o que passa da borda, então o brilho das
// células da primeira/última coluna e linha sumiria. Margem negativa devolve a
// largura que o padding tirou, e as células ficam exatamente do mesmo tamanho.
.dc-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  align-content: flex-start;
  gap: var(--dc-gap);
  min-height: 0;
  margin: calc(-12 * var(--dc-u));
  padding: calc(12 * var(--dc-u));
  overflow-y: auto;
  overflow-x: hidden;
  // Não encadeia o scroll pro fundo (sem rubber-band vazando pro body).
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
}

// Só 'loading' e 'error' passam por aqui — os dois estados sem acesso saem no
// modal de aviso.
.dc-state {
  @apply flex flex-col items-center gap-3 text-white/90 text-sm text-center;

  padding: 48px 0;
}

.dc-retry {
  @apply text-white text-sm underline;
}

// Desktop: card centrado na largura do Figma, em vez do bottom-sheet do
// mobile (mesma virada que a roletinha faz no .rlt-screen).
@media (min-width: 1024px) {
  .dc-overlay {
    @apply justify-center;

    padding: 12px;
  }

  .dc-frame {
    --dc-panel-w: 382px;
  }

  .dc-panel {
    padding-bottom: 16px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
  }
}

// ---------------------------------------------------------------------------
// Jornada finalizada (ENG-793): lista, acordeao do (i) e acoes. Tudo isto vive
// no slot do BaseNoticeSheet, ou seja, DENTRO do painel dele — por isso as
// medidas sao em px e nao em % (a regua ja e o painel, ver o comentario da
// folha sobre % resolverem pela largura).
// ---------------------------------------------------------------------------
.dc-news {
  @apply relative z-[1] list-none;

  margin: 20px 0 0;
  padding: 14px 14px 13px;
  border: 1px solid rgba(255, 201, 74, 0.22);
  border-radius: 13px;
  background: rgba(255, 201, 74, 0.07);
  text-align: left;
}

.dc-news__item {
  // `items-center` e nao `items-start`: o item de duas linhas ("Premios
  // diferentes...") deixava o icone colado no topo, desalinhado dos outros tres
  // que sao de uma linha so. Centrado, a bolinha fica no meio do bloco de texto
  // em qualquer quantidade de linhas.
  @apply flex items-center gap-[9px];

  color: #dee2ff;
  font-size: 12.5px;
  line-height: 1.35;

  & + & {
    margin-top: 9px;
  }
}

.dc-news__icon {
  @apply grid place-items-center shrink-0;

  width: 22px;
  height: 22px;
  border-radius: 99px;
  background: rgba(255, 201, 74, 0.18);
  color: #ffc94a;

  svg {
    width: 13px;
    height: 13px;
  }
}

// Acordeao do (i). A entrada e `animation` e nao `transition`: o reset global do
// main.scss zera transition no app inteiro (o mesmo motivo do pulso do selo na
// folha), entao um max-height transicionado nao animaria nada aqui.
.dc-why {
  @apply relative z-[1];

  margin-top: 16px;
  padding: 11px 13px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  text-align: left;
  animation: dc-why-in 0.32s cubic-bezier(0.22, 1, 0.36, 1);

  p {
    margin: 0;
    color: #aeb6e8;
    font-size: 12px;
    line-height: 1.55;
  }

  p + p {
    margin-top: 7px;
    color: #868fc8;
  }
}

@keyframes dc-why-in {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

@media (prefers-reduced-motion: reduce) {
  .dc-why {
    animation: none;
  }
}

.dc-acts {
  @apply relative z-[1] grid;

  gap: 8px;
  margin-top: 18px;
}

.dc-notify,
.dc-hub {
  @apply flex items-center justify-center;

  gap: 10px;
  padding: 13px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
}

.dc-notify__bell {
  @apply shrink-0;

  width: 16px;
  height: 16px;
}

// Convite: o amarelo da casa, o unico botao cheio da folha.
.dc-notify--cta {
  border: 0;
  background: linear-gradient(180deg, #ffe884, #f5b301);
  color: #3a2600;
  box-shadow:
    0 12px 26px -13px rgb(245, 179, 1),
    0 1px 0 rgba(255, 255, 255, 0.5) inset;

  &:disabled {
    opacity: 0.6;
  }
}

// Confirmado: div e nao botao (ver o template). Verde de estado, nao de acao.
.dc-notify--done {
  padding: 12px 14px;
  border: 1px solid rgba(61, 220, 132, 0.42);
  background: linear-gradient(180deg, #1d2483, #0b1046);
  text-align: left;

  img {
    @apply shrink-0;

    width: 18px;
    height: 18px;
  }

  span {
    @apply flex flex-col;

    gap: 2px;
  }

  b {
    color: #9fe6c0;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.25;
  }

  small {
    color: #9fa9e0;
    font-size: 11.5px;
    font-weight: 400;
    line-height: 1.35;
  }
}

.dc-hub {
  border: 1px solid rgba(140, 150, 255, 0.26);
  background: transparent;
  color: #c6ccff;
}

.dc-foot {
  @apply relative z-[1] text-center;

  margin-top: 14px;
  color: #646ca6;
  font-size: 10.5px;
  line-height: 15px;
}
</style>
